import { create } from 'zustand'
import type { RackLifecycleStatus, RackRole } from '../utils/rackLifecycle.logic'
import { operatorPositionToIndex } from '../utils/rackName.logic.ts'
import type { NamingProfileAssignments, NamingProfileCatalog } from '../utils/namingProfile.logic.ts'
import { DEFAULT_FLOOR_PLAN, type FloorPlanSettings } from '../utils/floorPlanGeometry.logic.ts'
import { placementFromSource } from '../utils/placement.logic.ts'
import type { Placement, FloorPlanning } from '../utils/placement.logic.ts'
import { validateStructuralPlacement } from '../utils/structuralGeometry.logic.ts'
import type { StructuralGeometry } from '../utils/structuralGeometry.logic.ts'
import { deriveAisle, derivedAisleZone } from '../utils/aisle.logic.ts'
import type { BackgroundUnderlay } from '../utils/underlayCalibration.logic.ts'
import type { MeasurementMode } from '../utils/measurement.logic.ts'
import { applyMask, buildMaskVars } from '../utils/naming.ts'
import { formatNamingProfile, resolveNamingProfileVersion } from '../utils/namingProfile.logic.ts'
import { isPositionOccupied } from '../utils/collisionGuard.logic.ts'

// ─── Constants ───────────────────────────────────────────────────────────────
export const CELL = 60          // px per grid cell (= 0.6 m at ~100px/m)
export const PPM  = 100         // pixels per meter

export type LensType = 'none' | 'temperature' | 'power' | 'space' | 'spof'
export type ToolType = 'select' | 'draw_tray' | 'draw_zone' | 'erase' | 'measure'
export type FloorEntityKind = 'rack' | 'cooling_unit' | 'power_unit' | 'architecture'
export type RackType = 'server_rack' | 'network_rack' | 'open_frame' | 'crac' | 'inrow_cooler' | 'ups' | 'pdu_frame' | 'pillar'
export type AisleType = 'hot' | 'cold' | 'containment' | 'indeterminate'
export type TrayType = 'overhead' | 'underfloor' | 'ladder' | 'wire_mesh' | 'fiber_raceway' | 'power_busway'
export type TrayMedia = 'fiber' | 'copper' | 'power'
export type RackOrientation = 0 | 90 | 180 | 270
export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
export type RowPositionKind = 'empty' | 'rack' | 'inrow_cooler' | 'reservation'
export type OperationalZoneKind = 'safety' | 'maintenance' | 'operational'
export interface RowPosition { id: string; position: number; kind?: RowPositionKind; label?: string }
export interface OperationalZone {
  id: string
  kind: OperationalZoneKind
  xM: number
  yM: number
  widthM: number
  depthM: number
  label?: string
  blocksPlacement?: boolean
}

// ─── Row (named group of racks) ──────────────────────────────────────────────
export interface RowConfig {
  id: string
  name: string           // user-visible name: "A", "Ряд 01", "Серверный"
  labelIndex: number     // 1-based ordering for mask {row} variable
  orientation: number    // 0 | 90 | 180 | 270 — shared orientation of all racks
  color?: string | null  // optional explicit highlight color, not a saved rendering default
  moduleId?: string | null // hierarchy scope: row belongs to one module
  axis?: 'horizontal' | 'vertical'
  head?: { xM: number; yM: number }
  tail?: { xM: number; yM: number }
  facing?: number
  sequenceDirection?: 'forward' | 'reverse'
  positions?: RowPosition[]
}

// ─── Global DC editor settings (stored in draft) ──────────────────────────────
export interface DcEditorSettings {
  planning?: FloorPlanning
  namingMask: string          // e.g. "{module}/{row:02d}/{rack:02d}"
  module: string              // "1" — module/building number
  hall: string                // "A" — hall/zone letter
  defaultRackWidthM: number   // 0.6
  defaultRackDepthM: number   // 1.0
  defaultRackHeightM: number  // 2.0  (42U × 44.45mm ≈ 1.87m, standard is 2.0m)
  defaultAisleWidthM: number  // 1.2  — cold/hot aisle width
  defaultRowSpacingM: number  // 0.0  — back-to-back gap between rows
  defaultTrayHeightM: number  // 0.3  — tray height above rack top
  trunkRackIndex?: number | null // vertical trunk index
  moduleCodes?: Record<string, string>
  namingProfiles?: NamingProfileCatalog
  namingAssignments?: NamingProfileAssignments
  floorPlan: FloorPlanSettings
  structuredGeometry: StructuralGeometry[]
  operationalZones: OperationalZone[]
  backgroundUnderlay?: BackgroundUnderlay | null
}

export const DEFAULT_DC_SETTINGS: DcEditorSettings = {
  namingMask: '{row_letter}-{rack:02d}',
  module: '1',
  hall: 'A',
  defaultRackWidthM: 0.6,
  defaultRackDepthM: 1.0,
  defaultRackHeightM: 2.0,
  defaultAisleWidthM: 1.2,
  defaultRowSpacingM: 0.0,
  defaultTrayHeightM: 0.3,
  trunkRackIndex: null,
  moduleCodes: {},
  floorPlan: DEFAULT_FLOOR_PLAN,
  structuredGeometry: [],
  operationalZones: [],
  backgroundUnderlay: null,
}

export function formatRowRackLabel(settings: DcEditorSettings, row: RowConfig, position: number): string {
  const moduleCode = row.moduleId ? settings.moduleCodes?.[row.moduleId] : undefined
  const moduleValue = moduleCode ?? settings.module
  if (settings.namingProfiles && settings.namingAssignments) {
    const profile = resolveNamingProfileVersion(settings.namingProfiles, settings.namingAssignments, {
      rowId: row.id,
      moduleId: row.moduleId ?? undefined,
    })
    const module = Number(moduleValue)
    if (Number.isInteger(module) && module > 0) {
      return formatNamingProfile(profile.profile, { module, row: row.labelIndex, rack: position })
    }
  }
  return applyMask(settings.namingMask, buildMaskVars({
    module: moduleValue,
    hall: settings.hall,
    row: row.labelIndex,
    rack: position,
  }))
}

function rowRackPlacement(
  rack: RackNode,
  row: RowConfig,
  position: number,
  racks: Record<string, RackNode>,
  settings: DcEditorSettings,
): { x: number; y: number } {
  const vertical = row.axis ? row.axis === 'vertical' : [90, 270].includes(((row.orientation % 360) + 360) % 360)
  const direction = row.sequenceDirection === 'reverse' ? -1 : 1
  const targetWidth = Math.max(0.01, rack.widthM || settings.defaultRackWidthM) * PPM
  const rowRacks = Object.values(racks)
    .filter(candidate => candidate.id !== rack.id && candidate.rowId === row.id && Number.isInteger(candidate.rackPosInRow))
    .sort((left, right) => (left.rackPosInRow! - right.rackPosInRow!))
  const predecessors = rowRacks.filter(candidate => candidate.rackPosInRow! < position)
  const predecessor = predecessors[predecessors.length - 1]
  const successor = rowRacks.find(candidate => candidate.rackPosInRow! > position)
  const widthAt = (slot: number) => {
    const placed = rowRacks.find(candidate => candidate.rackPosInRow === slot)
    return Math.max(0.01, placed?.widthM || settings.defaultRackWidthM) * PPM
  }
  const along = (candidate: RackNode) => vertical ? candidate.y : candidate.x
  const cross = rowRacks.length
    ? rowRacks.reduce((sum, candidate) => sum + (vertical ? candidate.x : candidate.y), 0) / rowRacks.length
    : undefined

  let axisPosition: number
  let crossPosition: number
  if (row.head) {
    const distance = direction > 0
      ? Array.from({ length: Math.max(0, position - 1) }, (_, index) => widthAt(index + 1)).reduce((sum, width) => sum + width, 0)
      : Array.from({ length: Math.max(0, position - 1) }, (_, index) => widthAt(index + 2)).reduce((sum, width) => sum + width, 0) + targetWidth
    axisPosition = (vertical ? row.head.yM : row.head.xM) * PPM + direction * distance
    crossPosition = (vertical ? row.head.xM : row.head.yM) * PPM
  } else if (predecessor) {
    axisPosition = along(predecessor) + direction * (direction > 0
      ? widthAt(predecessor.rackPosInRow!)
      : targetWidth)
    crossPosition = vertical ? predecessor.x : predecessor.y
  } else if (successor) {
    axisPosition = along(successor) - direction * (direction > 0
      ? targetWidth
      : widthAt(successor.rackPosInRow!))
    crossPosition = vertical ? successor.x : successor.y
  } else {
    axisPosition = along(rack)
    crossPosition = cross ?? (vertical ? rack.x : rack.y)
  }

  return vertical
    ? { x: Math.round(crossPosition), y: Math.round(axisPosition) }
    : { x: Math.round(axisPosition), y: Math.round(crossPosition) }
}

// ─── Aisle Zone (hot/cold corridor, containment) ─────────────────────────────
export interface AisleZone {
  id: string
  x: number           // canvas px top-left
  y: number           // canvas px top-left
  w: number           // canvas px width
  h: number           // canvas px height
  aisleType: AisleType
  label?: string      // user label, e.g. "Холодный коридор A"
  isDirty?: boolean
  isContained?: boolean
  derivedFromRowIds?: [string, string]
  derivedStatus?: 'cold' | 'hot' | 'indeterminate'
  derivedReason?: string
  widthM?: number
  typeOverride?: { aisleType: 'cold' | 'hot'; reason: string }
}

export interface RackNode {
  id: string
  x: number           // canvas px
  y: number           // canvas px
  width: number       // canvas px (legacy, kept for compat)
  height: number      // canvas px (legacy, kept for compat)
  rotation: number    // degrees (legacy canvas rotation)
  orientation: RackOrientation  // physical rack orientation: 0=North(front up) | 90=East | 180=South(front down) | 270=West
  label: string
  rackType: RackType
  uCount: number | null
  widthM?: number | null // physical width; null means unknown, unlike the canvas estimate
  depthM?: number | null // physical depth; null means unknown
  heightM?: number | null // physical cabinet height; null means unknown, independent of U
  tenant?: string | null // null means no assigned tenant
  status?: RackLifecycleStatus // 'planned' | 'active' | 'reserved' | 'deprecated'
  role?: RackRole              // 'Server' | 'Network' | 'Storage' | 'Colocation' | 'Edge' | 'Infrastructure'
  rowId?: string      // RowConfig.id — which row this rack belongs to
  moduleId?: string | null // ModuleConfig.id — inherited from the row scope
  rackPosInRow?: number // 1-based position within the row
  isDirty?: boolean   // created/moved in draft, not yet published
  rowIndex: number    // DC row_index — from API or auto-assigned
  rackIndex: number   // DC rack_index — from API or auto-assigned
  isFacility?: boolean // true for CRAC, In-Row, UPS, PDU, Pillar (no U-slots)
  isBlocked?: boolean  // true for physical obstacles like Pillar
  dbId?: number       // Actual integer ID from backend RackConfig
  max_power_w?: number | null
  max_weight_kg?: number | null
  cascadeTenancy?: boolean // Whether to propagate tenant to child equipment upon commit
  placement?: Placement // canonical metric placement; pixel fields remain a renderer adapter
}

export interface TrayNode {
  id: string
  name?: string | null
  x: number
  y: number
  z_m?: number
  type: 'terminal' | 'tee'
}

export interface TrayEdge {
  id: string
  name?: string | null
  label?: string | null // legacy display label, retained until explicitly renamed
  source: string      // TrayNode.id
  target: string      // TrayNode.id
  type?: 'copper' | 'fiber' | 'power' | string
  mediaType?: TrayMedia
  trayType?: TrayType // physical infrastructure type
  color: string
  widthM: number
  fillCalcEnabled?: boolean  // user opt-in: calculate cable fill capacity
  capacityProfile?: 'count' | 'fill_ratio'
  capacityEnforcement?: boolean // explicit second step after reviewing the audit
  capacityAreaMm2?: number
  fillRatio?: number
  cableAreaMm2?: number
  // fill calc fields (occupancy is refreshed from the backend)
  maxCables?: number
  usedCables?: number        // actual cables routed through this tray
  status?: 'planned' | 'active' | 'restricted' | 'out_of_service' | 'removed'
}

// ─── History snapshot ────────────────────────────────────────────────────────
interface HistoryEntry {
  racks: Record<string, RackNode>
  trayNodes: Record<string, TrayNode>
  trayEdges: Record<string, TrayEdge>
  rows: Record<string, RowConfig>
  aisleZones: Record<string, AisleZone>
  dcSettings: DcEditorSettings
}

// ─── State ───────────────────────────────────────────────────────────────────
export interface EditorState {
  showPlanning: boolean
  setShowPlanning: (visible: boolean) => void
  setPlanning: (planning: FloorPlanning) => void
  planRackMove: (id: string, rowId: string | undefined, position: number, xM: number, yM: number) => string | null
  // Stage transform
  stageX: number
  stageY: number
  stageScale: number
  stageW: number
  stageH: number
  setStageTransform: (x: number, y: number, scale: number) => void
  setStageSize: (w: number, h: number) => void

  // Tool
  tool: ToolType
  setTool: (t: ToolType) => void
  measurementMode: MeasurementMode
  setMeasurementMode: (mode: MeasurementMode) => void

  // Rack objects
  racks: Record<string, RackNode>
  setRacks: (racks: Record<string, RackNode>) => void
  baselineRacks: Record<string, RackNode>
  setBaselineRacks: (racks: Record<string, RackNode>) => void
  loadRacks: (racks: Record<string, RackNode>) => void
  addRack: (rack: RackNode) => void
  addRacksBatch: (racks: RackNode[]) => void
  updateRack: (id: string, data: Partial<RackNode>) => void
  batchUpdateRacks: (ids: string[], data: Partial<RackNode>) => void
  assignRackToRowPosition: (id: string, rowId: string, position: number) => boolean
  assignRacksToRow: (ids: string[], rowId: string) => boolean
  detachRackFromRow: (id: string) => boolean
  deleteRack: (id: string) => void
  moveRacks: (moves: { id: string; x: number; y: number }[]) => void
  deleteSelected: () => void
  duplicateSelected: () => void
  selectAll: () => void

  // Rows (named groups)
  rows: Record<string, RowConfig>
  addRow: (row: RowConfig) => void
  updateRow: (id: string, data: Partial<RowConfig>) => void
  deleteRow: (id: string) => void
  loadRows: (rows: Record<string, RowConfig>) => void

  // Row & Aisle Generator
  showRowGenerator: boolean
  setShowRowGenerator: (v: boolean) => void
  addGeneratedRowBatch: (payload: { rows: RowConfig[]; racks: RackNode[]; aisleZone?: AisleZone }) => void
  applyRowExtension: (payload: { row: RowConfig; additions: RackNode[]; updates: Array<{ id: string; patch: Partial<RackNode> }> }) => void
  applyRowSlot: (payload: { row: RowConfig; additions: RackNode[] }) => void
  applyRowCommand: (payload: { rows: RowConfig[]; deleteRowId?: string; rackUpdates: Array<{ id: string; patch: Partial<RackNode> }> }) => void
  applyRowFootprint: (payload: { row: RowConfig; rackId: string; targetPatch: Partial<RackNode>; rackUpdates: Array<{ id: string; patch: Partial<RackNode> }> }) => void

  // Matrix Hall Wizard
  showSetupWizard: boolean
  setShowSetupWizard: (v: boolean) => void
  applyMatrixHallBatch: (payload: { rows: Record<string, RowConfig>; racks: RackNode[]; aisleZones: Record<string, AisleZone>; replaceExisting: boolean }) => void

  // Rack Allocation Matrix
  showRackAllocation: boolean
  setShowRackAllocation: (v: boolean) => void
  applyRackAllocation: (allocations: Record<string, string>, cascadeToEquipment?: boolean) => void

  // Pathway Generator (Cable Trays)
  showPathwayGenerator: boolean
  setShowPathwayGenerator: (v: boolean) => void
  applyGeneratedPathwaysBatch: (payload: {
    nodes: Record<string, TrayNode>
    edges: Record<string, TrayEdge>
    replaceExisting: boolean
  }) => void

  // DC editor settings
  dcSettings: DcEditorSettings
  setDcSettings: (s: Partial<DcEditorSettings>) => void
  addStructuralGeometry: (structure: StructuralGeometry) => void
  loadDcSettings: (s: Partial<DcEditorSettings>) => void

  // Aisle Zones (hot/cold corridors, containment)
  aisleZones: Record<string, AisleZone>
  addAisleZone: (zone: AisleZone) => void
  updateAisleZone: (id: string, data: Partial<AisleZone>) => void
  removeAisleZone: (id: string) => void
  loadAisleZones: (zones: Record<string, AisleZone>) => void
  recomputeDerivedAisles: () => void
  addOperationalZone: (zone: OperationalZone) => void
  updateOperationalZone: (id: string, data: Partial<OperationalZone>) => void
  removeOperationalZone: (id: string) => void
  convertAisleZoneToOperational: (id: string, kind?: OperationalZoneKind) => void

  // Overlay visibility toggles
  showAisles: boolean
  setShowAisles: (v: boolean) => void
  showTrays: boolean
  setShowTrays: (v: boolean) => void
  showLabels: boolean
  setShowLabels: (v: boolean) => void
  showThermal: boolean
  setShowThermal: (v: boolean) => void

  // Elevation panel visibility
  showElevation: boolean
  setShowElevation: (v: boolean) => void

  // Tray graph (cable trays)
  trayNodes: Record<string, TrayNode>
  trayEdges: Record<string, TrayEdge>
  setTrayGraph: (nodes: Record<string, TrayNode>, edges: Record<string, TrayEdge>) => void
  addTrayNode: (node: TrayNode) => void
  addTrayEdge: (edge: TrayEdge) => void

  trayDrawType: TrayMedia
  setTrayDrawType: (t: TrayMedia) => void
  updateTrayEdge: (id: string, data: Partial<TrayEdge>) => void
  deleteTrayEdge: (id: string) => void
  deleteTrayNode: (id: string) => void
  selectedEdgeId: string | null
  setSelectedEdgeId: (id: string | null) => void

  // Selection
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  selectedRowId: string | null
  setSelectedRowId: (id: string | null) => void
  toggleSelected: (id: string, additive: boolean) => void
  clearSelection: () => void

  // Hover
  hoveredId: string | null
  setHoveredId: (id: string | null) => void

  // Lens
  activeLens: LensType
  setActiveLens: (lens: LensType) => void

  // Telemetry
  telemetryData: Record<string, any>
  setTelemetryData: (data: Record<string, any>) => void

  // What-If Simulation
  whatIfActive: boolean
  setWhatIfActive: (active: boolean) => void
  whatIfData: Record<string, any>
  setWhatIfData: (data: Record<string, any>) => void

  // Save status (shown in StatusBar)
  saveStatus: SaveStatus
  setSaveStatus: (s: SaveStatus) => void

  // History (Undo / Redo)
  _history: HistoryEntry[]
  _historyIdx: number
  pushHistory: () => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean

  // Palette rack sequence counter (starts at 10000, increments per palette-dropped rack)
  // Ensures unique row/rack indices that don't collide with real DB racks (0–~50 range)
  _paletteSeq: number

  // Draw-tray state (ephemeral)
  drawingTrayFromId: string | null
  setDrawingTrayFromId: (id: string | null) => void

  // Marquee selection state (ephemeral)
  marquee: { x: number; y: number; w: number; h: number } | null
  setMarquee: (m: { x: number; y: number; w: number; h: number } | null) => void

  // Collisions
  collidingIds: string[]
  setCollidingIds: (ids: string[]) => void

  // Clipboard
  _clipboard: RackNode[]
  copySelected: () => void
  pasteClipboard: () => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function snapshot(s: EditorState): HistoryEntry {
  return {
    racks: structuredClone(s.racks),
    trayNodes: structuredClone(s.trayNodes),
    trayEdges: structuredClone(s.trayEdges),
    rows: structuredClone(s.rows),
    aisleZones: structuredClone(s.aisleZones),
    dcSettings: structuredClone(s.dcSettings),
  }
}

function pushSnap(s: EditorState, snap: HistoryEntry) {
  const base = s._history.slice(0, s._historyIdx + 1)
  const next = [...base, snap].slice(-MAX_HISTORY)
  const nextIdx = next.length - 1
  return { _history: next, _historyIdx: nextIdx, canUndo: nextIdx > 0, canRedo: false }
}

function pushNewState(s: EditorState, updates: Partial<EditorState>) {
  const nextState = { ...s, ...updates } as EditorState
  const snap = snapshot(nextState)
  return { ...updates, ...pushSnap(s, snap) }
}

const MAX_HISTORY = 50

function derivedAislesFor(rows: Record<string, RowConfig>, racks: Record<string, RackNode>, previous: Record<string, AisleZone>) {
  const result: Record<string, AisleZone> = {}
  const grouped = new Map<string, Array<{ row: RowConfig; racks: RackNode[]; cross: number }>>()
  Object.values(rows).forEach(row => {
    const rowRacks = Object.values(racks).filter(rack => rack.rowId === row.id)
    if (!rowRacks.length) return
    const horizontal = row.axis ? row.axis === 'horizontal' : ![90, 270].includes(((row.orientation % 360) + 360) % 360)
    const cross = horizontal
      ? rowRacks.reduce((sum, rack) => sum + rack.y, 0) / rowRacks.length
      : rowRacks.reduce((sum, rack) => sum + rack.x, 0) / rowRacks.length
    const key = `${row.moduleId ?? ''}:${horizontal ? 'horizontal' : 'vertical'}`
    const list = grouped.get(key) ?? []
    list.push({ row, racks: rowRacks, cross })
    grouped.set(key, list)
  })

  grouped.forEach(list => {
    list.sort((a, b) => a.cross - b.cross || a.row.labelIndex - b.row.labelIndex)
    for (let index = 0; index < list.length - 1; index += 1) {
      const calculation = deriveAisle(list[index].row, list[index + 1].row, list[index].racks, list[index + 1].racks)
      const zone = derivedAisleZone(calculation)
      const oldZone = previous[zone.id]
      if (oldZone?.typeOverride && calculation.status !== 'indeterminate') zone.typeOverride = oldZone.typeOverride
      result[zone.id] = zone
    }
  })
  return result
}

// ─── Store ───────────────────────────────────────────────────────────────────
export const useEditorStore = create<EditorState>((set, get) => ({
  showPlanning: false,
  setShowPlanning: (showPlanning) => set({ showPlanning }),
  setPlanning: (planning) => set(s => pushNewState(s, {
    dcSettings: { ...s.dcSettings, planning }, saveStatus: 'dirty' as SaveStatus,
  })),
  planRackMove: (id, rowId, position, xM, yM) => {
    const s = get()
    const rack = s.racks[id]
    if (!rack?.dbId || rack.status !== 'active') return 'Планирование доступно для опубликованной действующей стойки'
    const row = rowId ? s.rows[rowId] : undefined
    if (rowId && (!row || !Number.isSafeInteger(position) || position < 1)) return 'Укажите существующий ряд и целое место от 1'
    if (row && (Object.values(s.racks).some(other => other.rowId === row.id && other.rackPosInRow === position) ||
      row.positions?.some(slot => slot.position === position && slot.kind !== 'empty' && slot.kind !== 'rack'))) return 'Целевое место занято или зарезервировано'
    const point = row ? rowRackPlacement(rack, row, position, s.racks, s.dcSettings) : { x: xM * PPM, y: yM * PPM }
    const planning = s.dcSettings.planning ?? { placements: [], rack_reservations: [] }
    const rotation = row?.orientation ?? rack.rotation
    const box = { id: `planned-${id}`, ...point, w: (rack.widthM ?? s.dcSettings.defaultRackWidthM) * PPM, h: (rack.depthM ?? s.dcSettings.defaultRackDepthM) * PPM, rotation }
    const obstacles = Object.values(s.racks).map(r => ({ id: r.id, x: r.x, y: r.y, w: (r.widthM ?? s.dcSettings.defaultRackWidthM) * PPM, h: (r.depthM ?? s.dcSettings.defaultRackDepthM) * PPM, rotation: r.rotation }))
    for (const plan of planning.placements) {
      if (plan.rack_id === rack.dbId) continue
      const source = Object.values(s.racks).find(r => r.dbId === plan.rack_id)
      if (rowId && plan.target.row_id === rowId && plan.target.rack_pos_in_row === position) return 'Целевое место уже запланировано'
      if (source) obstacles.push({ id: `planned-${source.id}`, x: plan.target.x_m * PPM, y: plan.target.y_m * PPM,
        w: (source.widthM ?? s.dcSettings.defaultRackWidthM) * PPM, h: (source.depthM ?? s.dcSettings.defaultRackDepthM) * PPM, rotation: plan.target.rotation })
    }
    const floor = s.dcSettings.floorPlan
    const check = validateStructuralPlacement(box, s.dcSettings.structuredGeometry, {
      widthM: floor.widthMm / 1000, heightM: floor.heightMm / 1000,
      originXM: floor.originXMm / 1000, originYM: floor.originYMm / 1000, ppm: PPM,
    })
    if (![point.x, point.y].every(Number.isFinite) || !check.valid || isPositionOccupied(box, obstacles)) return 'Цель пересекается с объектом, резервом или границей зала'
    const target = { x_m: +(point.x / PPM).toFixed(3), y_m: +(point.y / PPM).toFixed(3), rotation,
      row_id: rowId ?? null, rack_pos_in_row: rowId ? position : null }
    get().setPlanning({ ...planning, placements: [...planning.placements.filter(plan => plan.rack_id !== rack.dbId), { rack_id: rack.dbId, target }] })
    return null
  },
  // Stage
  stageX: 0, stageY: 0, stageScale: 1, stageW: 800, stageH: 600,
  setStageTransform: (x, y, scale) => set({ stageX: x, stageY: y, stageScale: scale }),
  setStageSize: (w, h) => set({ stageW: w, stageH: h }),

  // Tool
  tool: 'select',
  setTool: (t) => set({ tool: t }),
  measurementMode: 'direct',
  setMeasurementMode: (mode) => set({ measurementMode: mode }),

  // Tray Draw Type
  trayDrawType: 'fiber',
  setTrayDrawType: (t) => set({ trayDrawType: t }),

  // Racks
  racks: {},
  setRacks: (racks) => set({ racks }),

  // Baseline for calculating staged changes
  baselineRacks: {},
  setBaselineRacks: (racks) => set({ baselineRacks: racks }),

  // Load racks from API + reset history baseline so Undo doesn't go to empty state
  loadRacks: (racks) => set(s => ({
    racks,
    _history: [{
      racks: structuredClone(racks),
      trayNodes: structuredClone(s.trayNodes),
      trayEdges: structuredClone(s.trayEdges),
      rows: structuredClone(s.rows),
      aisleZones: structuredClone(s.aisleZones),
      dcSettings: structuredClone(s.dcSettings),
    }],
    _historyIdx: 0,
    canUndo: false,
    canRedo: false,
    saveStatus: 'idle' as SaveStatus,
  })),

  // Add rack — atomic: push history + add rack + increment palette seq in ONE set()
  addRacksBatch: (newRacks) => set(s => {
    const racks = { ...s.racks }
    newRacks.forEach(r => { racks[r.id] = r })
    return pushNewState(s, {
      racks,
      _paletteSeq: s._paletteSeq + newRacks.length,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  addRack: (rack) => set(s => pushNewState(s, {
    racks: { ...s.racks, [rack.id]: rack },
    // Increment palette sequence so next palette rack gets a unique rackIndex
    _paletteSeq: s._paletteSeq + 1,
    saveStatus: 'dirty' as SaveStatus,
  })),

  updateRack: (id, data) => set(s => {
    const current = s.racks[id]
    if (!current) return {}
    const updatedRack = { ...current, ...data }
    if (data.rowId !== undefined) {
      updatedRack.moduleId = data.rowId ? s.rows[data.rowId]?.moduleId : undefined
    }
    updatedRack.placement = placementFromSource({
      x: updatedRack.x,
      y: updatedRack.y,
      widthM: updatedRack.widthM,
      depthM: updatedRack.depthM,
      orientation: updatedRack.rotation,
    })
    if (data.widthM !== undefined) {
      updatedRack.width = Math.round((data.widthM ?? s.dcSettings.defaultRackWidthM) * PPM)
    }
    if (data.depthM !== undefined) {
      updatedRack.height = Math.round((data.depthM ?? s.dcSettings.defaultRackDepthM) * PPM)
    }
    const trayNodes = { ...s.trayNodes }
    if (trayNodes[id]) {
      const rw = (updatedRack.widthM || 0.6) * 100
      const rh = (updatedRack.depthM || 1.0) * 100
      trayNodes[id] = {
        ...trayNodes[id],
        x: updatedRack.x + rw / 2,
        y: updatedRack.y + rh / 2,
      }
    }
    return pushNewState(s, {
      racks: { ...s.racks, [id]: updatedRack },
      trayNodes,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Batch update multiple racks atomically (single undo step)
  batchUpdateRacks: (ids: string[], data: Partial<RackNode>) => set(s => {
    const racks = { ...s.racks }
    ids.forEach(id => {
      const r = racks[id]
      if (!r) return
      const updated = { ...r, ...data, isDirty: true }
      racks[id] = { ...updated, placement: placementFromSource(updated) }
    })
    return pushNewState(s, { racks, saveStatus: 'dirty' as SaveStatus })
  }),

  assignRackToRowPosition: (id, rowId, position) => {
    let assigned = false
    set(s => {
    const rack = s.racks[id]
    const row = s.rows[rowId]
    if (!rack || !row || !Number.isInteger(position) || position < 1) return {}
    const occupiedByRack = Object.values(s.racks).some(candidate => (
      candidate.id !== id && candidate.rowId === rowId && candidate.rackPosInRow === position
    ))
    const occupiedBySlot = (row.positions ?? []).some(slot => (
      slot.position === position && slot.kind !== 'empty' && slot.kind !== 'rack'
    ))
    if (occupiedByRack || occupiedBySlot) return {}

    const rows = { ...s.rows }
    if (rack.rowId && rack.rackPosInRow) {
      const sourceRow = rows[rack.rowId]
      if (sourceRow) {
        rows[rack.rowId] = {
          ...sourceRow,
          positions: (sourceRow.positions ?? []).map(slot => slot.position === rack.rackPosInRow
            ? { ...slot, kind: 'empty' as const, label: undefined }
            : slot),
        }
      }
    }
    const placement = rowRackPlacement(rack, row, position, s.racks, s.dcSettings)
    const candidate = {
      id,
      x: placement.x,
      y: placement.y,
      w: (rack.widthM || s.dcSettings.defaultRackWidthM) * PPM,
      h: (rack.depthM || s.dcSettings.defaultRackDepthM) * PPM,
      rotation: rack.rotation,
    }
    const obstacles = Object.values(s.racks)
      .filter(candidateRack => candidateRack.id !== id)
      .map(candidateRack => ({
        id: candidateRack.id,
        x: candidateRack.x,
        y: candidateRack.y,
        w: (candidateRack.widthM || s.dcSettings.defaultRackWidthM) * PPM,
        h: (candidateRack.depthM || s.dcSettings.defaultRackDepthM) * PPM,
        rotation: candidateRack.rotation,
      }))
    if (isPositionOccupied(candidate, obstacles)) return {}
    const updatedRack: RackNode = {
      ...rack,
      ...placement,
      rowId,
      moduleId: row.moduleId,
      rackPosInRow: position,
      rowIndex: operatorPositionToIndex(row.labelIndex),
      rackIndex: operatorPositionToIndex(position),
      label: formatRowRackLabel(s.dcSettings, row, position),
      isDirty: true,
    }
    const nextPositions = (rows[rowId]?.positions ?? []).filter(slot => slot.position !== position)
    nextPositions.push({ id: `${row.id}-position-${position}`, position, kind: 'rack', label: updatedRack.label })
    rows[rowId] = { ...rows[rowId], positions: nextPositions.sort((left, right) => left.position - right.position) }
    const trayNodes = { ...s.trayNodes }
    if (trayNodes[id]) {
      trayNodes[id] = {
        ...trayNodes[id],
        x: updatedRack.x + (updatedRack.widthM || 0.6) * PPM / 2,
        y: updatedRack.y + (updatedRack.depthM || 1) * PPM / 2,
      }
    }
    assigned = true
    return pushNewState(s, {
      racks: { ...s.racks, [id]: { ...updatedRack, placement: placementFromSource(updatedRack) } },
      rows,
      trayNodes,
      saveStatus: 'dirty' as SaveStatus,
    })
    })
    return assigned
  },

  assignRacksToRow: (ids, rowId) => {
    let assigned = false
    set(s => {
    const row = s.rows[rowId]
    if (!row) return {}
    const selected = new Set(ids)
    const position = Math.max(0, ...Object.values(s.racks)
      .filter(r => r.rowId === rowId && !selected.has(r.id))
      .map(r => r.rackPosInRow || 0))
    const occupied = new Set([
      ...Object.values(s.racks)
        .filter(r => r.rowId === rowId && !selected.has(r.id))
        .map(r => r.rackPosInRow || 0),
      ...(row.positions ?? [])
        .filter(slot => slot.kind !== 'empty' && slot.kind !== 'rack')
        .map(slot => slot.position),
    ])
    const vertical = row.axis
      ? row.axis === 'vertical'
      : [90, 270].includes(((row.orientation % 360) + 360) % 360)
    const direction = row.sequenceDirection === 'reverse' ? -1 : 1
    const rowIndex = operatorPositionToIndex(row.labelIndex)
    const ordered = ids
      .map(id => s.racks[id])
      .filter((r): r is RackNode => Boolean(r))
      .sort((a, b) => {
        const primary = vertical ? a.y - b.y : a.x - b.x
        const secondary = vertical ? a.x - b.x : a.y - b.y
        return direction * primary || secondary
      })
    const racks = { ...s.racks }
    const rows = { ...s.rows }
    const previewRacks = { ...racks }
    const plannedBoxes: Array<{ id: string; x: number; y: number; w: number; h: number; rotation?: number }> = []
    const plannedPositions = new Map<string, { position: number; x: number; y: number }>()
    let previewPosition = position
    for (const rack of ordered) {
      do { previewPosition += 1 } while (occupied.has(previewPosition))
      occupied.add(previewPosition)
      const placement = rowRackPlacement(rack, row, previewPosition, previewRacks, s.dcSettings)
      const candidate = {
        id: rack.id,
        x: placement.x,
        y: placement.y,
        w: (rack.widthM || s.dcSettings.defaultRackWidthM) * PPM,
        h: (rack.depthM || s.dcSettings.defaultRackDepthM) * PPM,
        rotation: rack.rotation,
      }
      const obstacles = Object.values(previewRacks)
        .filter(candidateRack => candidateRack.id !== rack.id && !selected.has(candidateRack.id))
        .map(candidateRack => ({
          id: candidateRack.id,
          x: candidateRack.x,
          y: candidateRack.y,
          w: (candidateRack.widthM || s.dcSettings.defaultRackWidthM) * PPM,
          h: (candidateRack.depthM || s.dcSettings.defaultRackDepthM) * PPM,
          rotation: candidateRack.rotation,
        }))
      if (isPositionOccupied(candidate, [...obstacles, ...plannedBoxes])) return {}
      plannedBoxes.push(candidate)
      plannedPositions.set(rack.id, { position: previewPosition, ...placement })
      previewRacks[rack.id] = { ...rack, ...placement, rowId, rackPosInRow: previewPosition }
    }
    Object.values(rows).forEach(sourceRow => {
      const movedPositions = new Set(Object.values(s.racks)
        .filter(rack => selected.has(rack.id) && rack.rowId === sourceRow.id)
        .map(rack => rack.rackPosInRow)
        .filter((value): value is number => Number.isInteger(value)))
      if (!movedPositions.size) return
      rows[sourceRow.id] = {
        ...sourceRow,
        positions: (sourceRow.positions ?? []).filter(slot => !movedPositions.has(slot.position)),
      }
    })
    ordered.forEach(r => {
      const planned = plannedPositions.get(r.id)
      if (!planned) return
      racks[r.id] = {
        ...r,
        x: planned.x,
        y: planned.y,
        rowId,
        moduleId: row.moduleId,
        rackPosInRow: planned.position,
        rowIndex,
        rackIndex: operatorPositionToIndex(planned.position),
        label: formatRowRackLabel(s.dcSettings, row, planned.position),
        isDirty: true,
      }
    })
    const positions = [...(rows[rowId].positions ?? [])]
    ordered.forEach(r => {
      const rack = racks[r.id]
      const existing = positions.findIndex(slot => slot.position === rack.rackPosInRow)
      const next = { id: `${row.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow!, kind: 'rack' as const, label: rack.label }
      if (existing >= 0) positions[existing] = next
      else positions.push(next)
    })
    rows[rowId] = { ...rows[rowId], positions: positions.sort((a, b) => a.position - b.position) }
    assigned = ordered.length > 0
    const trayNodes = { ...s.trayNodes }
    ordered.forEach(r => {
      const planned = plannedPositions.get(r.id)
      if (!planned) return
      const updatedRack = {
        ...r,
        x: planned.x,
        y: planned.y,
        widthM: r.widthM || s.dcSettings.defaultRackWidthM,
        depthM: r.depthM || s.dcSettings.defaultRackDepthM,
      }
      racks[r.id] = {
        ...racks[r.id],
        placement: placementFromSource(updatedRack),
      }
      if (trayNodes[r.id]) {
        trayNodes[r.id] = {
          ...trayNodes[r.id],
          x: planned.x + (updatedRack.widthM || 0.6) * PPM / 2,
          y: planned.y + (updatedRack.depthM || 1) * PPM / 2,
        }
      }
    })
    return pushNewState(s, { racks, rows, trayNodes, saveStatus: 'dirty' as SaveStatus })
    })
    return assigned
  },

  detachRackFromRow: (id) => {
    let detached = false
    set(s => {
      const rack = s.racks[id]
      if (!rack || (!rack.rowId && !Number.isInteger(rack.rackPosInRow))) return {}
      const rows = { ...s.rows }
      if (rack.rowId && Number.isInteger(rack.rackPosInRow)) {
        const row = rows[rack.rowId]
        if (row) {
          rows[rack.rowId] = {
            ...row,
            positions: (row.positions ?? []).map(slot => slot.position === rack.rackPosInRow
              ? { ...slot, kind: 'empty' as const, label: undefined }
              : slot),
          }
        }
      }
      const nextStagingIndex = Math.max(
        10000,
        ...Object.values(s.racks).map(candidate => Math.max(candidate.rowIndex || 0, candidate.rackIndex || 0) + 1),
      )
      detached = true
      return pushNewState(s, {
        racks: {
          ...s.racks,
          [id]: {
            ...rack,
            rowId: undefined,
            moduleId: undefined,
            rackPosInRow: undefined,
            rowIndex: nextStagingIndex,
            rackIndex: nextStagingIndex,
            isDirty: true,
          },
        },
        rows,
        saveStatus: 'dirty' as SaveStatus,
      })
    })
    return detached
  },

  // Delete rack — atomic: push history + delete in ONE set()
  deleteRack: (id) => set(s => {
    const deletedRack = s.racks[id]
    const racks = { ...s.racks }
    delete racks[id]
    const rows = { ...s.rows }
    if (deletedRack?.rowId && deletedRack.rackPosInRow) {
      const row = rows[deletedRack.rowId]
      if (row) {
        const positions = [...(row.positions ?? [])]
        const index = positions.findIndex(position => position.position === deletedRack.rackPosInRow)
        const empty = { id: `${row.id}-position-${deletedRack.rackPosInRow}`, position: deletedRack.rackPosInRow, kind: 'empty' as const }
        if (index >= 0) positions[index] = { ...positions[index], kind: 'empty', label: undefined }
        else positions.push(empty)
        rows[row.id] = { ...row, positions: positions.sort((left, right) => left.position - right.position) }
      }
    }
    // Проблема 15: каскадно удаляем TrayNode и все подключённые к нему рёбра
    const trayNodes = { ...s.trayNodes }
    const trayEdges = { ...s.trayEdges }
    if (trayNodes[id]) {
      delete trayNodes[id]
      Object.keys(trayEdges).forEach(eid => {
        const e = trayEdges[eid]
        if (e.source === id || e.target === id) delete trayEdges[eid]
      })
    }
    return pushNewState(s, {
      rows,
      racks,
      trayNodes,
      trayEdges,
      selectedIds: s.selectedIds.filter(sid => sid !== id),
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Move multiple racks atomically (history + positions in ONE set)
  moveRacks: (moves) => set(s => {
    const newRacks = { ...s.racks }
    const newTrayNodes = { ...s.trayNodes }
    moves.forEach(({ id, x, y }) => {
      if (!newRacks[id]) return
      newRacks[id] = {
        ...newRacks[id],
        x,
        y,
        placement: placementFromSource({ ...newRacks[id], x, y }),
        isDirty: true,
      }
      // Проблема 15: синхронно двигаем TrayNode (если он привязан к этой стойке по id)
      if (newTrayNodes[id]) {
        const rack = newRacks[id]
        newTrayNodes[id] = {
          ...newTrayNodes[id],
          x: x + ((rack.widthM || 0.6) * 100) / 2,
          y: y + ((rack.depthM || 1.0) * 100) / 2,
        }
      }
    })
    return pushNewState(s, {
      racks: newRacks,
      trayNodes: newTrayNodes,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Delete all selected racks, aisle zones and selected tray edge atomically
  deleteSelected: () => set(s => {
    if (!s.selectedIds.length && !s.selectedEdgeId) return {}
    const racks = { ...s.racks }
    const aisleZones = { ...s.aisleZones }
    const trayNodes = { ...s.trayNodes }
    const trayEdges = { ...s.trayEdges }
    const structuredGeometry = s.dcSettings.structuredGeometry.filter(
      structure => !s.selectedIds.includes(structure.id),
    )
    const operationalZones = s.dcSettings.operationalZones.filter(
      zone => !s.selectedIds.includes(zone.id),
    )
    let selectedEdgeId = s.selectedEdgeId

    if (s.selectedEdgeId && trayEdges[s.selectedEdgeId]) {
      delete trayEdges[s.selectedEdgeId]
      selectedEdgeId = null
    }

    s.selectedIds.forEach(id => {
      if (racks[id]) {
        delete racks[id]
        // Проблема 15: каскадное удаление TrayNode и связанных рёбер
        if (trayNodes[id]) {
          delete trayNodes[id]
          Object.keys(trayEdges).forEach(eid => {
            const e = trayEdges[eid]
            if (e.source === id || e.target === id) delete trayEdges[eid]
          })
        }
      }
      if (aisleZones[id]) {
        delete aisleZones[id]
      }
    })
    return pushNewState(s, {
      racks,
      aisleZones,
      trayNodes,
      trayEdges,
      dcSettings: { ...s.dcSettings, structuredGeometry, operationalZones },
      selectedIds: [],
      selectedEdgeId,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Duplicate all selected racks atomically (offset +1 cell)
  // Each duplicate gets a unique palette-range rackIndex so it's always INSERTed on publish
  duplicateSelected: () => set(s => {
    if (!s.selectedIds.length) return {}
    const newRacks = { ...s.racks }
    const newIds: string[] = []
    let seq = s._paletteSeq
    s.selectedIds.forEach(id => {
      const r = s.racks[id]
      if (!r) return
      const { dbId: _dbId, ...rackData } = r
      const newId = `dup-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      newRacks[newId] = {
        ...rackData,
        id: newId,
        x: r.x + CELL,
        y: r.y + CELL,
        placement: placementFromSource({ ...r, x: r.x + CELL, y: r.y + CELL }),
        label: r.label ? `${r.label} (копия)` : r.label,
        isDirty: true,
        // Unique palette indices — won't collide with original or other duplicates
        rowIndex: 10000,
        rackIndex: seq,
      }
      newIds.push(newId)
      seq++
    })
    return pushNewState(s, {
      racks: newRacks,
      selectedIds: newIds,
      _paletteSeq: seq,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Clipboard
  _clipboard: [],
  copySelected: () => set(s => {
    const copied = s.selectedIds.map(id => s.racks[id]).filter(Boolean)
    return { _clipboard: structuredClone(copied) }
  }),
  pasteClipboard: () => set(s => {
    if (!s._clipboard.length) return {}
    const suffix = window.prompt('Суффикс для имён вставленных объектов:', ' (копия)') ?? ' (копия)'
    const newRacks = { ...s.racks }
    const newIds: string[] = []
    let seq = s._paletteSeq
    s._clipboard.forEach(r => {
      const newId = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      const { dbId: _dbId, ...rackData } = r
      newRacks[newId] = {
        ...rackData,
        id: newId,
        x: r.x + CELL,
        y: r.y + CELL,
        label: r.label ? `${r.label}${suffix}` : r.label,
        isDirty: true,
        rowIndex: 10000,
        rackIndex: seq,
      }
      newIds.push(newId)
      seq++
    })
    return pushNewState(s, {
      racks: newRacks,
      selectedIds: newIds,
      _paletteSeq: seq,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Select all racks
  selectAll: () => set(s => ({ selectedIds: Object.keys(s.racks) })),

  // Aisle Zones
  aisleZones: {},
  addAisleZone: (zone) => set(s => pushNewState(s, {
    aisleZones: { ...s.aisleZones, [zone.id]: zone },
    saveStatus: 'dirty' as SaveStatus,
  })),
  updateAisleZone: (id, data) => set(s => pushNewState(s, {
    aisleZones: { ...s.aisleZones, [id]: { ...s.aisleZones[id], ...data } },
    saveStatus: 'dirty' as SaveStatus,
  })),
  removeAisleZone: (id) => set(s => {
    const aisleZones = { ...s.aisleZones }
    delete aisleZones[id]
    return pushNewState(s, { aisleZones, saveStatus: 'dirty' as SaveStatus })
  }),
  loadAisleZones: (zones) => set(s => {
    const newHistory = [...s._history]
    if (newHistory.length > 0 && s._historyIdx === 0) {
      newHistory[0] = {
        ...newHistory[0],
        aisleZones: structuredClone(zones),
      }
    }
    return { aisleZones: zones, _history: newHistory }
  }),
  recomputeDerivedAisles: () => set(s => {
    const manualZones = Object.fromEntries(
      Object.entries(s.aisleZones).filter(([, zone]) => !zone.derivedFromRowIds),
    )
    const nextAisles = { ...manualZones, ...derivedAislesFor(s.rows, s.racks, s.aisleZones) }
    const previous = JSON.stringify(s.aisleZones)
    if (previous === JSON.stringify(nextAisles)) return {}
    return pushNewState(s, { aisleZones: nextAisles, saveStatus: 'dirty' as SaveStatus })
  }),
  addOperationalZone: (zone) => set(s => pushNewState(s, {
    dcSettings: { ...s.dcSettings, operationalZones: [...s.dcSettings.operationalZones, zone] },
    saveStatus: 'dirty' as SaveStatus,
  })),
  updateOperationalZone: (id, data) => set(s => pushNewState(s, {
    dcSettings: {
      ...s.dcSettings,
      operationalZones: s.dcSettings.operationalZones.map(zone => zone.id === id ? { ...zone, ...data } : zone),
    },
    saveStatus: 'dirty' as SaveStatus,
  })),
  removeOperationalZone: (id) => set(s => pushNewState(s, {
    dcSettings: {
      ...s.dcSettings,
      operationalZones: s.dcSettings.operationalZones.filter(zone => zone.id !== id),
    },
    selectedIds: s.selectedIds.filter(selectedId => selectedId !== id),
    saveStatus: 'dirty' as SaveStatus,
  })),
  convertAisleZoneToOperational: (id, kind = 'operational') => set(s => {
    const source = s.aisleZones[id]
    if (!source || source.derivedFromRowIds) return {}
    const nextAisles = { ...s.aisleZones }
    delete nextAisles[id]
    const operationalZone: OperationalZone = {
      id: `operational-${id}`,
      kind,
      xM: source.x / PPM,
      yM: source.y / PPM,
      widthM: source.w / PPM,
      depthM: source.h / PPM,
      label: source.label || 'Операционная зона',
      blocksPlacement: false,
    }
    return pushNewState(s, {
      aisleZones: nextAisles,
      dcSettings: { ...s.dcSettings, operationalZones: [...s.dcSettings.operationalZones, operationalZone] },
      selectedIds: s.selectedIds.filter(selectedId => selectedId !== id),
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Overlay toggles
  showAisles: true,
  setShowAisles: (v) => set({ showAisles: v }),
  showTrays: true,
  setShowTrays: (v) => set({ showTrays: v }),
  showLabels: true,
  setShowLabels: (v) => set({ showLabels: v }),
  showThermal: false,
  setShowThermal: (v) => set({ showThermal: v }),

  // Tray graph
  trayNodes: {}, trayEdges: {},
  setTrayGraph: (nodes, edges) => set(s => {
    const newHistory = [...s._history]
    if (newHistory.length > 0 && s._historyIdx === 0) {
      newHistory[0] = {
        ...newHistory[0],
        trayNodes: JSON.parse(JSON.stringify(nodes)),
        trayEdges: JSON.parse(JSON.stringify(edges)),
      }
    }
    return { trayNodes: nodes, trayEdges: edges, _history: newHistory }
  }),
  // Mark dirty so auto-save triggers when trays are drawn
  addTrayNode: (node) => set(s => pushNewState(s, { trayNodes: { ...s.trayNodes, [node.id]: node }, saveStatus: 'dirty' as SaveStatus })),
  addTrayEdge: (edge) => set(s => {
    if (edge.source === edge.target) return s
    const isDup = Object.values(s.trayEdges).some(
      e => (e.source === edge.source && e.target === edge.target) ||
           (e.source === edge.target && e.target === edge.source)
    )
    if (isDup) return s
    return pushNewState(s, { trayEdges: { ...s.trayEdges, [edge.id]: edge }, saveStatus: 'dirty' as SaveStatus })
  }),
  updateTrayEdge: (id, data) => set(s => {
    if (!s.trayEdges[id]) return s;
    return pushNewState(s, {
      trayEdges: { ...s.trayEdges, [id]: { ...s.trayEdges[id], ...data } },
      saveStatus: 'dirty' as SaveStatus,
    });
  }),
  deleteTrayEdge: (id) => set(s => {
    const trayEdges = { ...s.trayEdges };
    delete trayEdges[id];
    return pushNewState(s, {
      trayEdges,
      selectedEdgeId: s.selectedEdgeId === id ? null : s.selectedEdgeId,
      saveStatus: 'dirty' as SaveStatus,
    });
  }),
  deleteTrayNode: (id) => set(s => {
    const trayNodes = { ...s.trayNodes };
    const trayEdges = { ...s.trayEdges };
    delete trayNodes[id];
    Object.keys(trayEdges).forEach(eid => {
      const e = trayEdges[eid];
      if (e.source === id || e.target === id) {
        delete trayEdges[eid];
      }
    });
    return pushNewState(s, {
      trayNodes,
      trayEdges,
      drawingTrayFromId: s.drawingTrayFromId === id ? null : s.drawingTrayFromId,
      saveStatus: 'dirty' as SaveStatus,
    });
  }),
  selectedEdgeId: null,
  setSelectedEdgeId: (id) => set(s => ({ selectedEdgeId: id, selectedIds: id ? [] : s.selectedIds })),

  // Selection
  selectedIds: [],
  selectedRowId: null,
  setSelectedRowId: (id) => set({ selectedRowId: id }),
  setSelectedIds: (ids) => set(s => ({ selectedIds: ids, selectedEdgeId: ids.length > 0 ? null : s.selectedEdgeId })),
  toggleSelected: (id, additive) => set(s => {
    if (additive) {
      const already = s.selectedIds.includes(id)
      return {
        selectedIds: already ? s.selectedIds.filter(x => x !== id) : [...s.selectedIds, id],
        selectedRowId: null,
        selectedEdgeId: null,
      }
    }
    return { selectedIds: [id], selectedRowId: null, selectedEdgeId: null }
  }),
  clearSelection: () => set({ selectedIds: [], selectedRowId: null, selectedEdgeId: null }),

  // Hover
  hoveredId: null,
  setHoveredId: (id) => set({ hoveredId: id }),

  // Lens
  activeLens: 'none',
  setActiveLens: (lens) => set({ activeLens: lens }),

  // Telemetry
  telemetryData: {},
  setTelemetryData: (data) => set({ telemetryData: data }),

  // What-If Simulation
  whatIfActive: false,
  setWhatIfActive: (active) => set({ whatIfActive: active }),
  whatIfData: {},
  setWhatIfData: (data) => set({ whatIfData: data }),

  // Save status
  saveStatus: 'idle',
  setSaveStatus: (s) => set({ saveStatus: s }),

  // History — seed with an empty snapshot so the very first action is undoable
  _history: [{ racks: {}, trayNodes: {}, trayEdges: {}, rows: {}, aisleZones: {}, dcSettings: structuredClone(DEFAULT_DC_SETTINGS) }],
  _historyIdx: 0,
  canUndo: false,
  canRedo: false,
  // Palette rack counter — starts high to avoid collision with real DB indices (0–~50)
  _paletteSeq: 10000,
  pushHistory: () => set(s => {
    const snap = snapshot(s)
    return pushSnap(s, snap)
  }),
  undo: () => set(s => {
    const idx = s._historyIdx - 1
    if (idx < 0) return {}
    const entry = s._history[idx]
    const safeSelectedEdgeId = (s.selectedEdgeId && entry.trayEdges[s.selectedEdgeId]) ? s.selectedEdgeId : null
    const safeSelectedIds = s.selectedIds.filter(id => Boolean(entry.racks[id]) || entry.dcSettings.structuredGeometry.some(g => g.id === id))
    // After undo, state is different from server — mark dirty so auto-save fires
    return {
      ...entry,
      selectedEdgeId: safeSelectedEdgeId,
      selectedIds: safeSelectedIds,
      drawingTrayFromId: null,
      _historyIdx: idx,
      canUndo: idx > 0,
      canRedo: true,
      saveStatus: 'dirty' as SaveStatus
    }
  }),
  redo: () => set(s => {
    const idx = s._historyIdx + 1
    if (idx >= s._history.length) return {}
    const entry = s._history[idx]
    const safeSelectedEdgeId = (s.selectedEdgeId && entry.trayEdges[s.selectedEdgeId]) ? s.selectedEdgeId : null
    const safeSelectedIds = s.selectedIds.filter(id => Boolean(entry.racks[id]) || entry.dcSettings.structuredGeometry.some(g => g.id === id))
    // After redo, state is different from server — mark dirty so auto-save fires
    return {
      ...entry,
      selectedEdgeId: safeSelectedEdgeId,
      selectedIds: safeSelectedIds,
      drawingTrayFromId: null,
      _historyIdx: idx,
      canUndo: idx > 0,
      canRedo: idx < s._history.length - 1,
      saveStatus: 'dirty' as SaveStatus
    }
  }),

  // Draw-tray ephemeral
  drawingTrayFromId: null,
  setDrawingTrayFromId: (id) => set({ drawingTrayFromId: id }),

  // Marquee
  marquee: null,
  setMarquee: (m) => set({ marquee: m }),

  // Rows (named groups of racks)
  rows: {},
  addRow: (row) => set(s => pushNewState(s, { rows: { ...s.rows, [row.id]: row }, saveStatus: 'dirty' as SaveStatus })),
  updateRow: (id, data) => set(s => {
    const rows = { ...s.rows, [id]: { ...s.rows[id], ...data } }
    if (!Object.prototype.hasOwnProperty.call(data, 'moduleId')) return pushNewState(s, { rows, saveStatus: 'dirty' as SaveStatus })
    const racks = { ...s.racks }
    Object.values(racks).forEach(rack => {
      if (rack.rowId === id) racks[rack.id] = { ...rack, moduleId: data.moduleId }
    })
    return pushNewState(s, { rows, racks, saveStatus: 'dirty' as SaveStatus })
  }),
  deleteRow: (id) => set(s => {
    const rows = { ...s.rows }
    delete rows[id]
    // Detach racks from this row
    const racks = { ...s.racks }
    Object.keys(racks).forEach(rid => {
      if (racks[rid].rowId === id) racks[rid] = { ...racks[rid], rowId: undefined, rackPosInRow: undefined }
    })
    return pushNewState(s, { rows, racks, saveStatus: 'dirty' as SaveStatus })
  }),
  loadRows: (rows) => set(s => {
    const newHistory = [...s._history]
    if (newHistory.length > 0 && s._historyIdx === 0) {
      newHistory[0] = {
        ...newHistory[0],
        rows: structuredClone(rows),
      }
    }
    return { rows, _history: newHistory }
  }),

  // DC editor settings
  dcSettings: { ...DEFAULT_DC_SETTINGS },
  setDcSettings: (s) => set(state => ({
    dcSettings: { ...state.dcSettings, ...s },
    saveStatus: 'dirty' as SaveStatus,
  })),
  addStructuralGeometry: (structure) => set(s => pushNewState(s, {
    dcSettings: {
      ...s.dcSettings,
      structuredGeometry: [...s.dcSettings.structuredGeometry, structure],
    },
    saveStatus: 'dirty' as SaveStatus,
  })),
  loadDcSettings: (s) => set(state => ({
    dcSettings: { ...state.dcSettings, ...s },
    _history: state._historyIdx === 0 && state._history.length > 0
      ? [{ ...state._history[0], dcSettings: structuredClone({ ...state.dcSettings, ...s }) }, ...state._history.slice(1)]
      : state._history,
  })),

  // Elevation panel
  showElevation: false,
  setShowElevation: (v) => set({ showElevation: v }),

  // Row & Aisle Generator
  showRowGenerator: false,
  setShowRowGenerator: (v) => set({ showRowGenerator: v }),
  addGeneratedRowBatch: (payload) => set(s => {
    const nextRows = { ...s.rows }
    payload.rows.forEach(r => { nextRows[r.id] = r })

    const nextRacks = { ...s.racks }
    payload.racks.forEach(r => { nextRacks[r.id] = r })

    const nextAisles = { ...s.aisleZones }
    if (payload.aisleZone) {
      nextAisles[payload.aisleZone.id] = payload.aisleZone
    }

    return pushNewState(s, {
      rows: nextRows,
      racks: nextRacks,
      aisleZones: nextAisles,
      _paletteSeq: s._paletteSeq + payload.racks.length,
      saveStatus: 'dirty' as SaveStatus,
      showRowGenerator: false,
    })
  }),
  applyRowExtension: ({ row, additions, updates }) => set(s => {
    const nextRacks = { ...s.racks }
    updates.forEach(({ id, patch }) => {
      const current = nextRacks[id]
      if (current) nextRacks[id] = { ...current, ...patch, placement: placementFromSource({ ...current, ...patch }) }
    })
    additions.forEach(rack => { nextRacks[rack.id] = rack })
    return pushNewState(s, {
      rows: { ...s.rows, [row.id]: row },
      racks: nextRacks,
      _paletteSeq: s._paletteSeq + additions.length,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),
  applyRowSlot: ({ row, additions }) => set(s => {
    const nextRacks = { ...s.racks }
    additions.forEach(rack => { nextRacks[rack.id] = rack })
    return pushNewState(s, {
      rows: { ...s.rows, [row.id]: row },
      racks: nextRacks,
      _paletteSeq: s._paletteSeq + additions.length,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),
  applyRowCommand: ({ rows, deleteRowId, rackUpdates }) => set(s => {
    const nextRows = { ...s.rows }
    rows.forEach(row => { nextRows[row.id] = row })
    if (deleteRowId) delete nextRows[deleteRowId]
    const nextRacks = { ...s.racks }
    rackUpdates.forEach(({ id, patch }) => {
      if (nextRacks[id]) nextRacks[id] = { ...nextRacks[id], ...patch, placement: placementFromSource({ ...nextRacks[id], ...patch }) }
    })
    return pushNewState(s, { rows: nextRows, racks: nextRacks, saveStatus: 'dirty' as SaveStatus })
  }),
  applyRowFootprint: ({ row, rackId, targetPatch, rackUpdates }) => set(s => {
    const nextRacks = { ...s.racks }
    const target = nextRacks[rackId]
    if (target) nextRacks[rackId] = { ...target, ...targetPatch, placement: placementFromSource({ ...target, ...targetPatch }) }
    rackUpdates.forEach(({ id, patch }) => {
      if (nextRacks[id]) nextRacks[id] = { ...nextRacks[id], ...patch, placement: placementFromSource({ ...nextRacks[id], ...patch }) }
    })
    return pushNewState(s, { rows: { ...s.rows, [row.id]: row }, racks: nextRacks, saveStatus: 'dirty' as SaveStatus })
  }),

  // Matrix Hall Wizard
  showSetupWizard: false,
  setShowSetupWizard: (v) => set({ showSetupWizard: v }),
  applyMatrixHallBatch: ({ rows, racks, aisleZones, replaceExisting }) => set(s => {
    const nextRows = replaceExisting ? { ...rows } : { ...s.rows, ...rows }
    const nextRacks = replaceExisting ? {} : { ...s.racks }
    racks.forEach(r => { nextRacks[r.id] = r })
    const nextAisles = replaceExisting ? { ...aisleZones } : { ...s.aisleZones, ...aisleZones }
    const nextTrayNodes = replaceExisting ? {} : s.trayNodes
    const nextTrayEdges = replaceExisting ? {} : s.trayEdges

    return pushNewState(s, {
      rows: nextRows,
      racks: nextRacks,
      aisleZones: nextAisles,
      trayNodes: nextTrayNodes,
      trayEdges: nextTrayEdges,
      selectedIds: [],
      _paletteSeq: s._paletteSeq + racks.length,
      saveStatus: 'dirty' as SaveStatus,
      showSetupWizard: false,
    })
  }),

  // Rack Allocation Matrix
  showRackAllocation: false,
  setShowRackAllocation: (v) => set({ showRackAllocation: v }),
  applyRackAllocation: (allocations, cascadeToEquipment = true) => set(s => {
    const racks = { ...s.racks }
    let changed = false
    Object.entries(allocations).forEach(([id, tenant]) => {
      const r = racks[id]
      if (r && (r.tenant || 'mine') !== tenant) {
        racks[id] = { ...r, tenant, isDirty: true, cascadeTenancy: cascadeToEquipment }
        changed = true
      }
    })
    if (!changed) return { showRackAllocation: false }
    return pushNewState(s, {
      racks,
      saveStatus: 'dirty' as SaveStatus,
      showRackAllocation: false,
    })
  }),

  // Pathway Generator (Cable Trays)
  showPathwayGenerator: false,
  setShowPathwayGenerator: (v) => set({ showPathwayGenerator: v }),
  applyGeneratedPathwaysBatch: ({ nodes, edges, replaceExisting }) => set(s => {
    const safeNodes = nodes || {}
    const safeEdges = edges || {}
    const nextTrayNodes = replaceExisting ? { ...safeNodes } : { ...s.trayNodes, ...safeNodes }

    const nextTrayEdges: Record<string, TrayEdge> = replaceExisting ? { ...safeEdges } : { ...s.trayEdges }
    if (!replaceExisting && safeEdges) {
      const existingPairs = new Set<string>()
      for (const e of Object.values(s.trayEdges)) {
        existingPairs.add(`${e.source}__${e.target}`)
        existingPairs.add(`${e.target}__${e.source}`)
      }
      for (const [eId, edge] of Object.entries(safeEdges)) {
        const pairKey = `${edge.source}__${edge.target}`
        if (!existingPairs.has(pairKey)) {
          nextTrayEdges[eId] = edge
          existingPairs.add(pairKey)
          existingPairs.add(`${edge.target}__${edge.source}`)
        }
      }
    }

    const nextSelectedEdgeId = (s.selectedEdgeId && nextTrayEdges[s.selectedEdgeId]) ? s.selectedEdgeId : null

    return pushNewState(s, {
      trayNodes: nextTrayNodes,
      trayEdges: nextTrayEdges,
      selectedEdgeId: nextSelectedEdgeId,
      drawingTrayFromId: null,
      showTrays: true,
      saveStatus: 'dirty' as SaveStatus,
      showPathwayGenerator: false,
    })
  }),

  // Collisions
  collidingIds: [],
  setCollidingIds: (ids) => set({ collidingIds: ids }),

  // Legacy compat shim
  get selectedId() { return get().selectedIds[0] ?? null },
}))
